import {
  collection,
  getDocs,
  writeBatch,
  doc,
  query,
  where,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { MundialTeam, MundialTeamInput, MUNDIAL_LEAGUE } from '../../types/mundial';
import * as XLSX from 'xlsx';

const COLLECTION = 'mundial_teams';

const HEADER_ALIASES: Record<string, keyof MundialTeamInput> = {
  nombre: 'name',
  name: 'name',
  seleccion: 'name',
  codigo: 'code',
  código: 'code',
  code: 'code',
  abreviatura: 'code',
  grupo: 'group',
  group: 'group',
  bandera: 'flag_url',
  flag: 'flag_url',
  flag_url: 'flag_url',
  url: 'flag_url',
  url_bandera: 'flag_url',
  link: 'flag_url',
  confederacion: 'confederation',
  confederación: 'confederation',
  confederation: 'confederation',
  orden: 'sort_order',
  order: 'sort_order',
  sort_order: 'sort_order',
  posicion: 'sort_order',
  posición: 'sort_order',
};

function parseSortOrder(value: unknown): number | undefined {
  if (value === '' || value === null || value === undefined) return undefined;
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function compareTeamsByGroupOrder(a: MundialTeam, b: MundialTeam): number {
  const groupCmp = a.group.localeCompare(b.group);
  if (groupCmp !== 0) return groupCmp;
  const orderA = a.sort_order ?? Number.MAX_SAFE_INTEGER;
  const orderB = b.sort_order ?? Number.MAX_SAFE_INTEGER;
  if (orderA !== orderB) return orderA - orderB;
  return a.name.localeCompare(b.name);
}

export function sortTeamsByGroupOrder(teams: MundialTeam[]): MundialTeam[] {
  return [...teams].sort(compareTeamsByGroupOrder);
}

export function getTeamsForGroup(teams: MundialTeam[], groupId: string): MundialTeam[] {
  return sortTeamsByGroupOrder(
    teams.filter((t) => t.group.toUpperCase() === groupId.toUpperCase())
  );
}

function normalizeHeader(h: string): string {
  return h
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

export function parseTeamsFromExcelBuffer(buffer: ArrayBuffer): {
  teams: MundialTeamInput[];
  errors: string[];
} {
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, { defval: '' });
  const teams: MundialTeamInput[] = [];
  const errors: string[] = [];
  const seenCodes = new Set<string>();
  /** Contador por grupo para orden automático si no viene columna Orden */
  const groupRowCount = new Map<string, number>();

  rows.forEach((row, index) => {
    const mapped: Partial<MundialTeamInput> & { sort_order?: number | string } = {};
    Object.entries(row).forEach(([key, value]) => {
      const field = HEADER_ALIASES[normalizeHeader(key)];
      if (field === 'sort_order') {
        mapped.sort_order = parseSortOrder(value);
      } else if (field) {
        mapped[field] = String(value).trim();
      }
    });

    const rowNum = index + 2;
    if (!mapped.name) {
      errors.push(`Fila ${rowNum}: falta el nombre de la selección`);
      return;
    }
    if (!mapped.code) {
      errors.push(`Fila ${rowNum}: falta el código o abreviatura`);
      return;
    }
    if (!mapped.group) {
      errors.push(`Fila ${rowNum}: falta el grupo`);
      return;
    }
    if (!mapped.flag_url) {
      errors.push(`Fila ${rowNum}: falta la URL o ruta de la bandera`);
      return;
    }

    const code = mapped.code.toUpperCase();
    if (seenCodes.has(code)) {
      errors.push(`Fila ${rowNum}: código duplicado "${code}"`);
      return;
    }
    seenCodes.add(code);

    const group = mapped.group.toUpperCase();
    if (mapped.sort_order === undefined) {
      const next = groupRowCount.get(group) ?? 0;
      mapped.sort_order = next;
      groupRowCount.set(group, next + 1);
    }

    teams.push({
      name: mapped.name,
      code,
      group,
      flag_url: mapped.flag_url,
      confederation: mapped.confederation,
      sort_order: mapped.sort_order,
    });
  });

  return { teams, errors };
}

export function validateTeams(teams: MundialTeamInput[]): string[] {
  const errors: string[] = [];
  const codes = new Set<string>();

  teams.forEach((t, i) => {
    if (codes.has(t.code)) {
      errors.push(`Selección duplicada: ${t.code} (fila ${i + 1})`);
    }
    codes.add(t.code);
    if (!/^[A-L]$/i.test(t.group)) {
      errors.push(`${t.name}: grupo "${t.group}" inválido (use A–L)`);
    }
  });

  return errors;
}

export const mundialTeamService = {
  async getAllTeams(): Promise<MundialTeam[]> {
    const ref = collection(db, COLLECTION);
    const q = query(ref, where('league', '==', MUNDIAL_LEAGUE));
    const snap = await getDocs(q);
    return sortTeamsByGroupOrder(
      snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          sort_order: data.sort_order ?? undefined,
          created_at: data.created_at?.toDate?.() ?? data.created_at,
          updated_at: data.updated_at?.toDate?.() ?? data.updated_at,
        } as MundialTeam;
      })
    );
  },

  async saveTeams(teams: MundialTeamInput[]): Promise<void> {
    const validationErrors = validateTeams(teams);
    if (validationErrors.length > 0) {
      throw new Error(validationErrors.join('\n'));
    }

    const batch = writeBatch(db);
    const now = new Date();

    teams.forEach((team, index) => {
      const ref = doc(collection(db, COLLECTION));
      batch.set(ref, {
        league: MUNDIAL_LEAGUE,
        name: team.name,
        code: team.code.toUpperCase(),
        group: team.group.toUpperCase(),
        flag_url: team.flag_url,
        confederation: team.confederation || null,
        sort_order: team.sort_order ?? index,
        created_at: now,
        updated_at: now,
      });
    });

    await batch.commit();
  },

  async replaceAllTeams(teams: MundialTeamInput[]): Promise<void> {
    const existing = await this.getAllTeams();
    await Promise.all(existing.map((t) => deleteDoc(doc(db, COLLECTION, t.id))));
    await this.saveTeams(teams);
  },

  getTeamsByCode(teams: MundialTeam[]): Map<string, MundialTeam> {
    return new Map(teams.map((t) => [t.code.toUpperCase(), t]));
  },
};
